import { useMemo, useState } from "react";
import { Reveal, SectionHead, Wrap } from "./Motion";

const Flag = ({ code, place, size = 20 }) => (
  <img src={`https://flagcdn.com/w40/${code}.png`} srcSet={`https://flagcdn.com/w80/${code}.png 2x`} width={size} height={Math.round(size * 0.75)} alt={place} className="object-cover border border-white/20 shrink-0" style={{ width: size, height: Math.round(size * 0.75) }} loading="lazy" />
);

const Card = ({ g, i, className = "" }) => (
  <article className={`group relative overflow-hidden bg-[#0F1D3D] border border-white/8 card-hover ${className}`} data-testid={`graduate-card-${i}`}>
    <div className="flex items-center gap-2.5 px-4 py-2.5 bg-[#0B1630] border-b border-[#D4AF37]/20" data-testid={`graduate-country-strip-${i}`}>
      <Flag code={g.country} place={g.place} />
      <span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F0]">{g.place}</span>
      <span className="ml-auto font-mono text-[10px] text-[#6F7C97]">{String(i + 1).padStart(2, "0")}</span>
    </div>
    <div className="relative aspect-[4/5] overflow-hidden">
      <img src={g.image} alt={`${g.horse} competing`} className="h-full w-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1630] via-[#0B1630]/30 to-transparent" />
      <div className="absolute bottom-0 inset-x-0 p-5">
        <p className="eyebrow !text-[#8FD174] inline-block bg-[#0B1630]/85 backdrop-blur px-2 py-1 leading-relaxed">{g.result}</p>
        <h3 className="mt-2 font-display text-2xl lg:text-3xl text-[#F8F6F0] leading-tight">{g.horse}</h3>
        {g.rider !== "—" && <p className="mt-1 text-xs text-[#AEB7C9]">with {g.rider}</p>}
      </div>
    </div>
    <div className="px-5 py-3.5 border-t border-white/5 flex items-center gap-2">
      <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
      <p className="text-[11px] tracking-[0.08em] text-[#AEB7C9]">{g.arc}</p>
    </div>
  </article>
);

export const Graduates = ({ graduates = [] }) => {
  const [country, setCountry] = useState("all");
  const countries = useMemo(() => {
    const m = new Map();
    graduates.forEach((g) => m.set(g.country, { code: g.country, place: g.place, n: (m.get(g.country)?.n || 0) + 1 }));
    return [...m.values()].sort((a, b) => b.n - a.n);
  }, [graduates]);
  const list = (country === "all" ? graduates : graduates.filter((g) => g.country === country)).slice(0, 12);

  return (
    <section id="graduates" className="relative py-20 lg:py-36 bg-[#0F1D3D] overflow-hidden" data-testid="graduates-section">
      <Wrap>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHead eyebrow="Where they are now" id="graduates" title="Graduates winning under other flags." sub="The only honest review of an auction is what its horses do twelve months later." />
          <Reveal delay={0.15}>
            <div className="flex gap-8 lg:gap-12">
              {[["37+", "Countries sold to"], ["75+", "Championship horses"], ["4", "Continents"]].map(([v, l]) => (
                <div key={l}><p className="font-display text-3xl lg:text-4xl text-[#D4AF37]">{v}</p><p className="text-[10px] tracking-[0.18em] uppercase text-[#6F7C97]">{l}</p></div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-8 snap-row lg:flex-wrap lg:overflow-visible -mx-5 px-5 lg:mx-0 lg:px-0 gap-2" data-testid="graduate-country-filters">
            <button onClick={() => setCountry("all")} data-testid="graduate-filter-all"
              className={`px-4 py-2 text-[10px] tracking-[0.18em] uppercase border transition-all duration-300 ${country === "all" ? "bg-[#D4AF37] text-[#0B1630] border-[#D4AF37]" : "border-white/15 text-[#AEB7C9] hover:border-[#D4AF37]/50"}`}>All countries</button>
            {countries.map((c) => (
              <button key={c.code} onClick={() => setCountry(c.code)} data-testid={`graduate-filter-${c.code}`} aria-pressed={country === c.code}
                className={`inline-flex items-center gap-2 px-3.5 py-2 text-[10px] tracking-[0.18em] uppercase border transition-all duration-300 ${country === c.code ? "bg-[#D4AF37] text-[#0B1630] border-[#D4AF37]" : "border-white/15 text-[#AEB7C9] hover:border-[#D4AF37]/50 hover:text-[#F8F6F0]"}`}>
                <Flag code={c.code} place={c.place} size={18} /> {c.place} <span className="opacity-60">{c.n}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 snap-row lg:hidden -mx-5 px-5" data-testid="graduates-snap-row">
          {list.map((g, i) => <Card key={g.horse} g={g} i={i} className="w-[76vw] max-w-[340px]" />)}
        </div>

        <div className="hidden lg:grid grid-cols-4 gap-6 mt-12" data-testid="graduates-grid">
          {list.map((g, i) => (
            <Reveal key={g.horse} delay={(i % 4) * 0.08}>
              <div className={i % 2 === 1 ? "lg:mt-12" : ""}><Card g={g} i={i} /></div>
            </Reveal>
          ))}
        </div>
        {list.length === 0 && <p className="mt-8 text-sm text-[#6F7C97]" data-testid="graduates-empty">No graduates listed for this country yet.</p>}
      </Wrap>
    </section>
  );
};
